import { Container, Flex, Box, Grid } from "theme-ui";
import { HTMLAttributes, ReactNode } from "react";
import { MasterDisclaimer } from "../../components/MasterDisclaimer";
import { Icon } from "../../components/Icons";
import { useStaticQuery, graphql, Link } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";

interface FooterSectionHeadingProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Choose the HTML element to render
   * @default 'p'
   * */
  className?: string;
  hideFooterLinks?: boolean;
  hideMasterDisclaimers?: boolean;
  hideMainFooterLinks?: boolean;
  disclaimers?: (string | ReactNode)[];
}

/**
 * The current footer - gives navigation links
 */
export const FooterNew: React.FC<FooterSectionHeadingProps> = ({
  className,
  hideFooterLinks,
  hideMasterDisclaimers,
  hideMainFooterLinks,
  disclaimers,
}) => {
  const data = useStaticQuery(
    graphql`
      query {
        appStore: file(
          relativePath: { eq: "layouts/legacy/app-button-apple-2.png" }
        ) {
          childImageSharp {
            fixed(width: 130, height: 40) {
              ...GatsbyImageSharpFixed_withWebp_noBase64
            }
          }
        }
        playStore: file(
          relativePath: { eq: "layouts/legacy/app-button-google-2.png" }
        ) {
          childImageSharp {
            fixed(width: 130, height: 40) {
              ...GatsbyImageSharpFixed_withWebp_noBase64
            }
          }
        }
      }
    `
  );

  const mainFooterLinks = [
    {
      title: "FAQs",
      link: "/faqs/",
    },
    // {
    //   title: "Cash Money Cares",
    //   link: "/cares/",
    // },
    {
      title: "Careers",
      link: "/careers/",
    },
    {
      title: "Find a Store Near Me",
      link: "/find-a-store/",
    },
    {
      title: "Contact Us",
      link: "/contact-us/",
    },
  ];
  const secondaryLinks = [
    {
      title: "Privacy Policy",
      link: "/privacy-policy/",
    },
    {
      title: "Terms of Use",
      link: "/terms-of-use/",
    },

    {
      title: "Communications Preferences",
      link: "/communications-preferences/",
    },
    {
      title: "Accessibility Information",
      link: "/accessibility/",
    },
  ];
  return (
    <Box
      as="footer"
      sx={{
        borderTop: "5px solid #645B88",
        borderTopWidth: "5px",
        borderImage:
          "linear-gradient(to right, #645B88, #2E368F, #42368E, #D91828, #FFDD00) 1 stretch",
        color: "brand.textBlack",
      }}
    >
      <Box
        sx={{
          bg: "#2C3644",
        }}
      >
        <Container
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: ["column", null, null, "row"],
            alignItems: "center",
            px: "15px",
          }}
        >
          <Container
            sx={{
              p: 0,
              pt: [4, null, null, 5],
              display: ["flex", null, null, "flex"],
              flexDirection: ["column"],
              alignItems: ["center", null, null, "flex-start"],
              justifyContent: "center",
              maxWidth: 450,
            }}
          >
            <StaticImage
              src="../../images/CM_Logo_White 1.png"
              alt="Cash Money White Logo"
              sx={{
                mb: 1,
              }}
            />
            <Box
              sx={{
                width: 180,
                display: "flex",
                mt: [2],
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <a href="https://www.facebook.com/cashmoney.ca/">
                <StaticImage
                  src="../../images/social/Facebook.png"
                  alt="Facebook Logo"
                />
              </a>
              <a href="https://www.instagram.com/cash_money1992/">
                <StaticImage
                  src="../../images/social/Instagram.png"
                  alt="Instagram Logo"
                />
              </a>
              <a href="https://www.linkedin.com/company/cash-money/">
                <StaticImage
                  src="../../images/social/LinkedIn.png"
                  alt="LinkedIn Logo"
                />
              </a>
            </Box>
            <p
              sx={{
                variant: "text.BodyNew",
                fontWeight: 600,
                color: "white",
                textAlign: ["center", null, null, "left"],
                pt: 4,
              }}
            >
              © 1992 - 2024 All Rights Reserved. Cash Money{" "}
            </p>
          </Container>
          <Container
            sx={{
              p: 3,
              color: "white",
              py: [3, null, null, 2],
            }}
          >
            <Flex
              sx={{
                width: "100%",
                justifyContent: ["center", null, null, "space-between"],
                alignItems: ["center", null, null, "center"],
                flexDirection: ["column", null, null, "row"],
                pt: [4],
              }}
            >
              {!hideMainFooterLinks && (
                <Box
                  sx={{
                    borderLeft: ["none", null, null, "1.5px solid white"],
                    height: 141,
                    pl: [0, null, null, 4],
                    pb: [4, null, null, 0],
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "column",
                    alignItems: ["center", null, null, "flex-start"],
                  }}
                >
                  {mainFooterLinks.map((links, i) => (
                    <li
                      key={i}
                      sx={{
                        listStyle: "none",
                      }}
                    >
                      <Link
                        sx={{
                          variant: "text.bodyNew",
                          color: "white",
                          fontWeight: 800,
                        }}
                        to={links.link}
                      >
                        {links.title}
                      </Link>
                    </li>
                  ))}
                </Box>
              )}
              <Box
                sx={{
                  borderLeft: ["none", null, null, "1.5px solid white"],
                  borderTop: ["1.5px solid white", null, null, "none"],
                  height: ["100%", null, null, 141],
                  width: [180, null, null, 300],
                  pl: [0, null, null, 4],
                  pt: [4, null, null, 0],
                  pb: [4, null, null, 0],
                  display: "flex",
                  justifyContent: "center",
                  flexDirection: "column",
                  textAlign: ["center", null, null, "left"],
                  alignItems: ["center", null, null, "flex-start"],
                }}
              >
                {secondaryLinks.map((links, i) => (
                  <li
                    key={i}
                    sx={{
                      listStyle: "none",
                    }}
                  >
                    <Link
                      sx={{
                        variant: "text.bodyNew",
                        color: "white",
                        fontWeight: 800,
                      }}
                      to={links.link}
                    >
                      {links.title}
                    </Link>
                  </li>
                ))}
              </Box>
            </Flex>
          </Container>
        </Container>

        <Container sx={{ px: "15px", pt: [0, null, null, 3] }}>
          <div
            sx={{
              variant: "text.disclaimer",
              "p:not(:last-of-type)": {
                mb: "20px",
              },
            }}
          >
            {!hideMasterDisclaimers && <MasterDisclaimer color="white" />}
            {disclaimers?.map((d, i) => (
              <p
                key={i}
                sx={{
                  color: "white",
                }}
              >
                {d}
              </p>
            ))}
          </div>
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: ["space-between", "flex-start"],
              mt: [4],
            }}
          >
            <Box>
              <a
                href="https://www.trustedsite.com/verify?host=www.cashmoney.ca&test=Y"
                rel="noopener noreferrer"
                target="_blank"
              >
                <Icon
                  name={"trusted-site-black"}
                  alt={"Trusted Site"}
                  viewBox={"0 0 87.744 36.138"}
                  width={80}
                />
              </a>
            </Box>
            {/* <Box sx={{ mx: 2 }}>
              <a
                href="https://canadiancfa.com/?utm_source=cashmoney&utm_medium=website&utm_campaign=footer"
                rel="noopener noreferrer"
                target="_blank"
              >
                <StaticImage
                  src="../../../images/ccfa-logo.png"
                  alt="CCFA Logo"
                  quality={100}
                  placeholder="none"
                  width={91}
                />
              </a>
            </Box> */}
          </Flex>
        </Container>
      </Box>
    </Box>
  );
};
